import * as go from 'gojs';
import { Warnings } from './warningPanel';
import theme from 'theme/theme';
import { store } from 'createStore';
import { TableRowPanel } from './TableRowPanel';
import { GoJsFlowGraph } from './goJsGraphPanel';
import type { UnitInfoTextInterface } from 'gojs/nodeTexts';
import { getTitleStroke } from './baseUnitUtils';
import { getFont } from '../utils/font';
import { PanelNames } from '../utils/panelNames';
import type {
  FlowPropertiesGraphPanelInterface,
  FlowPropertiesPxxTablePanelInterface,
  FlowPropertiesSizeTablePanelInterface,
  WarningPanelInterface,
} from 'gojs/panelData';
import type { Options } from '../utils';
import {
  defaultImageSize,
  defaultMargins,
  getDefaultPictureSize,
  getImageHeight,
  getImageWidth,
} from '../utils';
import { UnitTypes } from 'unitData/types';

const $ = go.GraphObject.make;

export const warnings = $(
  go.Panel,
  'Vertical',
  { alignment: go.Spot.Left },
  $(
    go.TextBlock,
    {
      alignment: go.Spot.Left,
      spacingBelow: 4,
      margin: new go.Margin(0, 0, 8, 0),
    },
    getTitleStroke(),
    new go.Binding('text', '', (data: WarningPanelInterface) => {
      const title = data.unitTextArray
        .flatMap((t: any) => t.texts)
        .find((t: any) => t.title);
      return `${title.text} - Warnings`;
    }),
    getFont({ weight: 700 }),
  ),
  Warnings('majorWarnings', 'major'),
  Warnings('moderateWarnings', 'moderate'),
  Warnings('minorWarnings', 'minor'),
);

export const diamond = () =>
  $(
    go.Shape,
    'RoundedRectangle',
    {
      width: 16,
      height: 16,
      parameter1: 2,
      strokeWidth: 0,
      angle: 45,
    },
    new go.Binding('alignment', 'mirrored', (mirrored) =>
      mirrored ? go.Spot.TopLeft : go.Spot.TopRight,
    ),
    new go.Binding(
      'fill',
      '',
      ({
        majorWarnings,
        moderateWarnings,
        minorWarnings,
        hideNonMajorWarnings,
      }) => {
        if (majorWarnings[0].warnings?.length > 0) {
          return theme.palette.warnings.major;
        } else if (
          moderateWarnings[0].warnings?.length > 0 &&
          !hideNonMajorWarnings
        ) {
          return theme.palette.warnings.moderate;
        } else if (
          minorWarnings[0].warnings?.length > 0 &&
          !hideNonMajorWarnings
        ) {
          return theme.palette.warnings.minor;
        } else {
          return 'rgba(0,0,0,0)';
        }
      },
    ),
  );

export const parallelUnitsDisplay = () =>
  $(
    go.Panel,
    'Auto',
    new go.Binding('alignment', 'mirrored', (mirrored) =>
      mirrored ? go.Spot.TopRight : go.Spot.TopLeft,
    ),
    $(
      go.Shape,
      'RoundedRectangle',
      {
        width: 26,
        height: 20,
        parameter1: 2,
        strokeWidth: 0,
        margin: 12,
      },
      new go.Binding('fill', '', ({ parallelUnits }) => {
        if (parallelUnits > 1) {
          return theme.palette.grey[800];
        } else {
          return 'rgba(0,0,0,0)';
        }
      }),
    ),
    $(
      go.TextBlock,
      {
        stroke: 'white',
        font: 'normal normal 500 12px Sandvik Sans Text',
        alignment: go.Spot.Center,
        margin: new go.Margin(0, 0, 0, 0),
      },
      new go.Binding('text', '', ({ parallelUnits }) => {
        if (parallelUnits > 1) {
          return `x${parallelUnits.toString()}`;
        } else return null;
      }),
    ),
  );

export const bottleNeckDisplay = () =>
  $(
    go.Panel,
    'Auto',
    { alignment: go.Spot.Center },
    $(
      go.Shape,
      'RoundedRectangle',
      {
        width: 30,
        height: 30,
        parameter1: 2,
        strokeWidth: 0,
        margin: 12,
        angle: 45,
      },
      new go.Binding('fill', '', (data) => {
        const bottleNeck =
          store?.getState().settingsState?.plantDisplay?.bottleNeck;
        if (bottleNeck && data.highestLoadUnit?.key === data.key) {
          return theme.palette.info.light;
        }
        return 'rgba(0,0,0,0)';
      }),
    ),
    $(
      go.TextBlock,
      {
        stroke: theme.palette.info.main,
        font: 'normal normal 500 10px Sandvik Sans Text',
        alignment: go.Spot.Center,
      },
      new go.Binding('text', '', (data) => {
        const bottleNeck =
          store?.getState().settingsState?.plantDisplay?.bottleNeck;
        if (bottleNeck && data.highestLoadUnit?.key === data.key) {
          return `${data.highestLoadUnit.load}%`;
        }
        return null;
      }),
    ),
  );

export const flowPropertiesSizeTable = $(
  go.Panel,
  'Vertical',
  $(
    go.TextBlock,
    {
      alignment: go.Spot.Left,
      name: PanelNames.FLOW_PROPERTIES_SIZE_TABLE_TITLE,
    },
    getTitleStroke(),
    new go.Binding(
      'visible',
      '',
      ({ unitTextArray }: FlowPropertiesSizeTablePanelInterface) => {
        const texts: UnitInfoTextInterface[] = unitTextArray[0].texts;
        const titles = texts
          .filter(
            (unitInfoText: UnitInfoTextInterface) =>
              unitInfoText.title === true,
          )
          .map((t) => t.text);
        return titles.length > 0;
      },
    ),
    new go.Binding(
      'text',
      '',
      ({ unitTextArray }: FlowPropertiesSizeTablePanelInterface) => {
        const texts: UnitInfoTextInterface[] = unitTextArray[0].texts;
        const titles = texts
          .filter(
            (unitInfoText: UnitInfoTextInterface) =>
              unitInfoText.title === true,
          )
          .map((t) => t.text);
        return titles.join('\n');
      },
    ),
    new go.Binding(
      'font',
      '',
      (data: FlowPropertiesSizeTablePanelInterface) => {
        const canvasBodyFontSize = data.canvasBodyFontSize.size;
        return `normal normal 700 ${canvasBodyFontSize}px Sandvik Sans Text`;
      },
    ),
  ),
  $(
    go.TextBlock,
    {
      stroke: theme.palette.grey[800],

      alignment: go.Spot.Left,
      margin: new go.Margin(0, 0, 8, 0),
    },
    new go.Binding(
      'text',
      '',
      ({ metricName }: FlowPropertiesSizeTablePanelInterface) => {
        return metricName;
      },
    ),
    new go.Binding(
      'font',
      '',
      (data: FlowPropertiesSizeTablePanelInterface) => {
        const canvasBodyFontSize = data.canvasBodyFontSize.size;
        return `normal normal 500 ${canvasBodyFontSize}px Sandvik Sans Text`;
      },
    ),
  ),
  $(
    go.Panel,
    'Vertical',
    { alignment: go.Spot.Left },
    new go.Binding(
      'itemArray',
      'flowPropertiesSizeTable',
      (
        flowPropertiesSizeTable: FlowPropertiesSizeTablePanelInterface['flowPropertiesSizeTable'],
      ) => flowPropertiesSizeTable,
    ),
    {
      itemTemplate: TableRowPanel,
    },
  ),
);

export const flowPropertiesPxxTable = $(
  go.Panel,
  'Vertical',
  $(
    go.TextBlock,
    {
      alignment: go.Spot.Left,
      name: PanelNames.FLOW_PROPERTIES_PXX_TABLE_TITLE,
    },
    getTitleStroke(),
    getFont({ weight: 700 }),
    new go.Binding(
      'visible',
      '',
      ({ unitTextArray }: FlowPropertiesPxxTablePanelInterface) => {
        const texts: UnitInfoTextInterface[] = unitTextArray[0].texts;
        const titles = texts
          .filter(
            (unitInfoText: UnitInfoTextInterface) =>
              unitInfoText.title === true,
          )
          .map((t) => t.text);
        return titles.length > 0;
      },
    ),
    new go.Binding(
      'text',
      '',
      ({ unitTextArray }: FlowPropertiesPxxTablePanelInterface) => {
        const texts: UnitInfoTextInterface[] = unitTextArray[0].texts;
        const titles = texts
          .filter(
            (unitInfoText: UnitInfoTextInterface) =>
              unitInfoText.title === true,
          )
          .map((t) => t.text);
        return titles.join('\n');
      },
    ),
    new go.Binding('font', '', (data: FlowPropertiesPxxTablePanelInterface) => {
      const canvasBodyFontSize = data.canvasBodyFontSize.size;
      return `normal normal 700 ${canvasBodyFontSize}px Sandvik Sans Text`;
    }),
  ),
  $(
    //Text added if Feed unit
    go.TextBlock,
    {
      stroke: theme.palette.grey[800],
      alignment: go.Spot.Left,
    },
    new go.Binding(
      'visible',
      '',
      ({ unitType }: FlowPropertiesPxxTablePanelInterface) =>
        unitType === 'FEED',
    ),
    new go.Binding(
      'text',
      '',
      ({ inputFlow }: FlowPropertiesPxxTablePanelInterface) =>
        inputFlow?.psdName,
    ),
    new go.Binding('font', '', (data: FlowPropertiesPxxTablePanelInterface) => {
      const canvasBodyFontSize = data.canvasBodyFontSize.size;
      return `normal normal 500 ${canvasBodyFontSize}px Sandvik Sans Text`;
    }),
  ),
  $(
    go.Panel,
    'Vertical',
    { alignment: go.Spot.Left, margin: new go.Margin(8, 0, 0, 0) },
    new go.Binding(
      'itemArray',
      'flowPropertiesPxxTable',
      (
        flowPropertiesPxxTable: FlowPropertiesPxxTablePanelInterface['flowPropertiesPxxTable'],
      ) => flowPropertiesPxxTable,
    ),
    {
      itemTemplate: TableRowPanel,
    },
  ),
);

export const flowPropertiesGraph = $(
  go.Panel,
  'Viewbox',
  {
    width: 375,
    height: 300,
    name: 'FLOW_PROPERTIES',
    maxSize: new go.Size(1000, 800),
    minSize: new go.Size(250, 200),
  },
  new go.Binding('height', 'height').makeTwoWay(),
  new go.Binding('width', 'width').makeTwoWay(),
  $(
    go.Panel,
    'Vertical',
    $(
      go.TextBlock,
      {
        alignment: go.Spot.Left,
        margin: new go.Margin(0, 0, 8, 0),
        name: PanelNames.FLOW_PROPERTIES_GRAPH_TITLE,
      },
      getTitleStroke(),
      getFont({ weight: 700, scale: 1.5 }),
      new go.Binding(
        'visible',
        '',
        ({ unitTextArray }: FlowPropertiesGraphPanelInterface) => {
          const texts: UnitInfoTextInterface[] = unitTextArray[0].texts;
          const titles = texts
            .filter(
              (unitInfoText: UnitInfoTextInterface) =>
                unitInfoText.title === true,
            )
            .map((t) => t.text);
          return titles.length > 0;
        },
      ),
      new go.Binding(
        'text',
        '',
        ({ unitTextArray }: FlowPropertiesGraphPanelInterface) => {
          const texts: UnitInfoTextInterface[] = unitTextArray[0].texts;
          const titles = texts
            .filter(
              (unitInfoText: UnitInfoTextInterface) =>
                unitInfoText.title === true,
            )
            .map((t) => t.text);
          return titles.join(' - ');
        },
      ),
    ),
    $(
      go.Panel,
      'Viewbox',
      $(
        go.Picture,
        new go.Binding('element', 'flowPropertiesGraph', GoJsFlowGraph),
      ),
    ),
  ),
);

export const parallelUnits = (
  imageBinding: () => go.Binding,
  mirroredImageBinding: () => go.Binding,
  options?: Options,
) => {
  const { height, width } = getDefaultPictureSize(
    options?.imageSize ?? defaultImageSize.square,
    options?.imageMargin ?? defaultMargins,
  );

  const imageTemplate = (index: number) => {
    return $(
      go.Panel,
      'Position',
      $(
        go.Picture,
        new go.Binding('visible', '', (node) => {
          return node.unitType === UnitTypes.STORAGE_FEEDER_2_OUTPUTS ||
            (UnitTypes.STORAGE_FEEDER && node.storageWith1OutputImg === 1)
            ? false
            : node.parallelUnits > index;
        }),
        new go.Binding('position', '', (data, _node) => {
          const p = data.parallelUnits;
          const mirror = data.mirrored ? -1 : 1;
          const scale = data.mirrored ? 0.8 : 0.85;
          const multiplier = Math.pow(scale, p - index);

          const x =
            (300 - 300 * multiplier) * mirror * (data?.useHighBin ? 1.7 : 1);
          const y = 100 * multiplier * (data?.useHighBin ? 2.2 : 1);

          return new go.Point(x, y);
        }),
        imageBinding(),
        mirroredImageBinding(),
        new go.Binding('height', '', (data) => {
          return (
            getImageHeight(data, height) *
            Math.pow(0.9, data.parallelUnits - index)
          );
        }),
        new go.Binding('width', '', (data) => {
          return (
            getImageWidth(data, width) *
            Math.pow(0.9, data.parallelUnits - index)
          );
        }),
      ),
    );
  };

  return $(
    go.Panel,
    'Spot',
    new go.Binding('alignment', 'mirrored', (mirrored) => {
      if (mirrored) {
        return new go.Spot(0.3, -0.04);
      }
      return new go.Spot(0.5, -0.05);
    }),
    imageTemplate(1),
    imageTemplate(2),
    imageTemplate(3),
    imageTemplate(4),
    imageTemplate(5),
    imageTemplate(6),
    imageTemplate(7),
    imageTemplate(8),
    imageTemplate(9),
    imageTemplate(10),
    imageTemplate(11),
  );
};

export const BaseUnit = () => {
  return [diamond(), parallelUnitsDisplay(), bottleNeckDisplay()];
};
